import React, { useMemo } from 'react';
import { Button, Typography, Box } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ConfigurableList, { ListSection } from '../../atoms/ConfigurableList';
import { ListItem } from '../../atoms/ConfigurableList/ListItem';
import { ViewType } from '../../organisms/workItemsTable';
import theme from '../../../theme/Theme';
import { NAVIGATION_COLORS } from '../NavigationColumn/constants';

interface WorklistNavigationContentProps {
    searchTerm: string;
    views: ViewType[];
    activeView?: ViewType;
    onViewChange: (view: ViewType) => void;
    roles: string[];
    onManageViewsClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

const WorklistNavigationContent: React.FC<WorklistNavigationContentProps> = ({
    searchTerm,
    views,
    activeView,
    onViewChange,
    roles,
    onManageViewsClick,
}) => {
    // Check if user has coding-related roles
    const isCodingManager = roles?.includes('coding_manager');
    const hasCodingRole = roles?.some(
        (role) =>
            role === 'coding_manager' ||
            role === 'coder' ||
            role === 'cqa_coder' ||
            role === 'cqa_manager' ||
            role !== 'professional_services'
    );

    const categorizeViews = useMemo(() => {
        const favoritesViews: ViewType[] = [];
        const myWorkViews: ViewType[] = [];
        const myTeamsWorkViews: ViewType[] = [];
        const savedViews: ViewType[] = [];

        const isCoder = roles?.includes('coder');
    
        const isMyWorkView = (name: string) => {
            if (isCoder && ['My Current', 'My Recent', 'My Holds'].includes(name)) {
                return true;
            }

            if (isCodingManager && name === 'Manager Review') {
                return true;
            }

            return false;
        };

        const isTeamWorkView = (name: string) => {
            if (isCodingManager && ['QA', 'Current', 'Recent', 'On Hold'].includes(name)) {
                return true;
            }

            return false;
        };

        const filteredViews = searchTerm
            ? views.filter(v =>
                v.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            : views;

        filteredViews.forEach((view) => {
            if (view.userPreferences?.isHidden) return;

            const name = view.name;

            if (isMyWorkView(name)) {
                myWorkViews.push(view);
                return;
            }

            if (isTeamWorkView(name)) {
                myTeamsWorkViews.push(view);
                return;
            }

            savedViews.push(view);
        });

        return { favoritesViews, myWorkViews, myTeamsWorkViews, savedViews };
    }, [views, isCodingManager,roles, searchTerm]);

    const { favoritesViews, myWorkViews, myTeamsWorkViews, savedViews } = categorizeViews;
   
    const createViewListItem = (view: ViewType) => {
        const isSelected = activeView?.viewId === view.viewId;
        return (
            <Box key={view.viewId} sx={{ position: 'relative' }}>
                <ListItem
                    title={view.name}
                    onClick={() => onViewChange(view)}
                    selected={isSelected}
                    textColor={NAVIGATION_COLORS.TEXT_PRIMARY}
                    selectedTextColor={NAVIGATION_COLORS.TEXT_SELECTED}
                    selectedBackgroundColor={NAVIGATION_COLORS.SELECTED_BG}
                    hoverBackgroundColor={NAVIGATION_COLORS.HOVER_BG}
                    sx={{
                        padding: '12px 16px 12px 12px',
                        borderRadius: '3px',
                        margin: '0 12px 4px 12px',
                        '& .MuiListItemText-primary': {
                            fontWeight: isSelected ? 600 : 400,
                            fontSize: '14px',
                            lineHeight: '16px',
                        },
                    }}
                />
                {isSelected && (
                    <ChevronRightIcon
                        sx={{
                            position: 'absolute',
                            right: '16px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: 18,
                            color: NAVIGATION_COLORS.TEXT_LOW,
                            pointerEvents: 'none',
                        }}
                    />
                )}
            </Box>
        );
    };

    const sections: ListSection[] = [];

    // FAVORITES Section - Always show (even if empty for now)
    if (favoritesViews.length > 0) {
        sections.push({
            title: "FAVORITES",
            rows: favoritesViews.map(createViewListItem),
            headerStyle: {
                padding: sections.length === 0 ? '8px 16px 4px 16px' : '16px 16px 4px 16px',
                color: theme.palette.artisticTextLow.main,
                fontSize: '9.72px',
                fontWeight: 500,
                letterSpacing: '0.5px'
            }
        });
    }

    // MY WORK Section - Show if user has coding role and there are views
    if (hasCodingRole && myWorkViews.length > 0) {
        sections.push({
            title: "MY WORK",
            rows: myWorkViews.map(createViewListItem),
            headerStyle: {
                padding: sections.length === 0 ? '8px 16px 4px 16px' : '16px 16px 4px 16px',
                color: theme.palette.artisticTextLow.main,
                fontSize: '9.72px',
                fontWeight: 500,
                letterSpacing: '0.5px'
            }
        });
    }

    // MY TEAM'S WORK Section - Show only for coding managers
    if (isCodingManager && myTeamsWorkViews.length > 0) {
        sections.push({
            title: "MY TEAM'S WORK",
            rows: myTeamsWorkViews.map(createViewListItem),
            headerStyle: {
                padding: '16px 16px 4px 16px',
                color: theme.palette.artisticTextLow.main,
                fontSize: '9.72px',
                fontWeight: 500,
                letterSpacing: '0.5px'
            }
        });
    }

    // SAVED VIEWS Section
    if (savedViews.length > 0) {
        const isFirstSection = sections.length === 0;
        sections.push({
            component: () => (
                <Box
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: isFirstSection ? '8px 16px 4px 16px' : '16px 16px 4px 16px'
                    }}
                >
                    <Typography
                        variant="overline"
                        style={{
                            color: theme.palette.artisticTextLow.main,
                            fontWeight: 500,
                            fontSize: '9.72px',
                            letterSpacing: '0.5px',
                            lineHeight: '12px',
                            textTransform: 'uppercase'
                        }}
                    >
                        SAVED VIEWS
                    </Typography>
                    {onManageViewsClick && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => onManageViewsClick(e)}
                            sx={{
                                color: NAVIGATION_COLORS.TEXT_SELECTED,
                                borderColor: NAVIGATION_COLORS.TEXT_SELECTED,
                                textTransform: 'none',
                                fontSize: '11px',
                                padding: '2px 8px',
                                minHeight: 'unset',
                                '&:hover': {
                                    borderColor: NAVIGATION_COLORS.TEXT_SELECTED,
                                    backgroundColor: 'rgba(229, 146, 125, 0.08)',
                                },
                            }}
                        >
                            Manage Views
                        </Button>
                    )}
                </Box>
            ),
            rows: savedViews.map(createViewListItem)
        });
    }

    return (
        <ConfigurableList
            sections={sections}
            containerStyle={{
                backgroundColor: 'transparent',
                margin: 0,
                padding: 0,
            }}
            textColor={theme.palette.artisticTextLow.main}
        />
    );
};

export default WorklistNavigationContent;
